import { useRef, useState } from 'react';

import { HOMEPAGE_VIDEO_TIMEOUT_IN_MILLIS } from '@web-platform/shared-universal-constants/src/adverts';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { logOperationalEvent } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import type { VideoTrackingDeps, VideoTrackingHandlers } from './videoTracking.types';

type Deps = Omit<VideoTrackingDeps, 'getVideo'> & { getVideo: () => HTMLVideoElement | null };

/**
 * Legacy `<video>` element's tracking. `AD_STOPPED` reads `watchDuration` from the DOM
 * element directly, accumulated across native loops via `viewCounter`. `AD_COMPLETED`
 * fires from the native `onEnded` event, gated on `isFullScreen && viewCounter === 0` so
 * only a deliberate, fullscreen watch-through counts (not a background collapsed loop).
 *
 * @returns {VideoTrackingHandlers} handlers to wire onto the legacy `<video>` element and its surrounding component
 */
export const useLegacyVideoTracking = ({
  creativeId,
  getVideo,
  isFullScreen,
}: Deps): VideoTrackingHandlers => {
  const [viewCounter, setViewCounter] = useState(0);
  // Legacy measures its own load window: onLoadStart captures a start timestamp, and
  // onCanPlayThrough checks it fired within budget. hasShownFallbackRef guards firing
  // AD_FALLBACK_SHOWN more than once per creative.
  const startTimeRef = useRef(0);
  const hasShownFallbackRef = useRef(false);
  const timedOutRef = useRef(false);

  const trackShowingFallbackContent = () => {
    if (hasShownFallbackRef.current) {
      return;
    }
    hasShownFallbackRef.current = true;
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_FALLBACK_SHOWN.NAME,
      strings: {
        creativeId,
      },
    });
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_FALLBACK_SHOWN.NAME,
      lineItemId: creativeId,
    });
  };

  const emitStoppedMetric = (action: string) => {
    const video = getVideo();
    if (!video) {
      return;
    }
    const { currentTime, duration } = video;
    const totalWatchDuration = duration * viewCounter + currentTime;
    const videoViewType = isFullScreen ? 'expanded' : 'collapsed';

    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_STOPPED.NAME,
      strings: {
        creativeId,
        action,
        videoViewType,
      },
      numbers: {
        watchDuration: totalWatchDuration,
      },
    });
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_STOPPED.NAME,
      lineItemId: creativeId,
      action,
      videoViewType,
      watchDuration: totalWatchDuration,
    });
  };

  return {
    onPause: () => emitStoppedMetric('pause'),
    onRedirect: () => emitStoppedMetric('redirect'),
    onExitFullscreen: () => {
      emitStoppedMetric('close');
      setViewCounter(0);
    },
    notePauseInitiated: () => {},
    onEnterFullscreen: () => {},
    onEnded: (isFullScreenAtEnd: boolean) => {
      if (isFullScreenAtEnd) {
        if (viewCounter === 0) {
          trackInteractionEvent({
            name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_COMPLETED.NAME,
            strings: {
              creativeId,
            },
          });
          logOperationalEvent({
            eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_COMPLETED.NAME,
            lineItemId: creativeId,
          });
        }
        setViewCounter(viewCounter + 1);
      }
    },
    onLoadStart: () => {
      startTimeRef.current = performance.now();
    },
    // Returns whether canPlayThrough landed within the timeout budget. `false` (still
    // within budget) means the component should mark ready; `true` means it timed out —
    // the component should clear the video src and keep showing the poster.
    onCanPlayThrough: () => {
      if (performance.now() - startTimeRef.current <= HOMEPAGE_VIDEO_TIMEOUT_IN_MILLIS) {
        return false;
      }
      timedOutRef.current = true;
      trackShowingFallbackContent();
      return true;
    },
    didTimeOut: () => timedOutRef.current,
  };
};
